const {parseRecommendedDefinition} = require("./recommended-sort");
const {
    GROUP_FILTER_KEY,
    getFilterGroups,
    resolveGroupFilters,
} = require("./filter-groups");

const normalizeProductListSort = (value) => {
    let values;
    if (Array.isArray(value)) {
        if (!value.length || value.some(item => typeof item !== 'string')) return null;
        values = [...value];
    } else {
        if (typeof value !== 'string') return null;
        try {
            values = decodeURIComponent(value).split(';');
        } catch {
            return null;
        }
    }

    values = values.map(item => item.trim());
    if (values.length === 3 && values[2] === '') values.pop();
    if (values.length !== 2) return null;

    const [field, direction] = values;
    if (!field || !['asc', 'desc'].includes(direction)) return null;

    return [field, direction];
};

const buildFiltersFromParam = (setActiveFilters, urlParams, fieldsList = []) => {
    fieldsList = [...new Set([...fieldsList, 'keyword-search', 'page', 'sort', GROUP_FILTER_KEY])];
    const newActiveFilters = {};
    const params = urlParams ?? window.location.search.slice(1);
    if (params) {
        const filters = params.split('&');
        let hasSortParam = false;
        filters.forEach(filter => {
            let key = filter.split('=')[0];
            const hasMalformedSortKey = key.startsWith('sort;');
            if (hasMalformedSortKey) key = 'sort';
            let values = filter.split('=')[1];
            
            // Only filter with available fields.
            if (!fieldsList.includes(key)) return;
            if (key === 'sort') {
                if (hasSortParam) {
                    delete newActiveFilters.sort;
                    return;
                }
                hasSortParam = true;
                const separatorIndex = filter.indexOf('=');
                const sort = normalizeProductListSort(
                    !hasMalformedSortKey && separatorIndex >= 0 ? filter.slice(separatorIndex + 1) : undefined
                );
                if (sort) newActiveFilters.sort = sort;
                else delete newActiveFilters.sort;
                return;
            }
            if (key === 'page' && (isNaN(+values) || +values < 1)) values = '1';
            try {
                values = values.split(';').map(value => decodeURIComponent(value));
            } catch {
                return;
            }
            newActiveFilters[key] = []
            values.length > 1
                ? values.forEach(v => {
                    if (v) newActiveFilters[key].push(v)
                })
                : newActiveFilters[key] = values[0]
        })
    }
    setActiveFilters(newActiveFilters)
}

const DEFAULT_RECOMMENDED_SORT = ['recommended', 'desc'];
const PRICE_DOC_VALUE_ACCESS_PATTERN = /doc\[['"](sellingPrice|discountedSellingPrice|unitPrice)['"]\]\.value/g;

const hasPriceDocValueAccess = (source) => {
    PRICE_DOC_VALUE_ACCESS_PATTERN.lastIndex = 0;
    return PRICE_DOC_VALUE_ACCESS_PATTERN.test(source);
};

const guardPriceDocValueAccess = (source) => {
    PRICE_DOC_VALUE_ACCESS_PATTERN.lastIndex = 0;
    return String(source).replace(
        PRICE_DOC_VALUE_ACCESS_PATTERN,
        (_, field) => `(doc.containsKey('${field}') && doc['${field}'].size() > 0 ? doc['${field}'].value : 0)`
    );
};

const sanitiseSortDefinition = (value) => {
    if (Array.isArray(value)) return value.map(sanitiseSortDefinition);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, sanitiseSortDefinition(item)]));
    }
    if (typeof value === 'string' && hasPriceDocValueAccess(value)) {
        return guardPriceDocValueAccess(value);
    }
    return value;
};

const getDefinitionSort = (value) => {
    const sort = parseRecommendedDefinition(value).sort;
    const clauses = (Array.isArray(sort) ? sort : [sort]).filter(clause => (
        (typeof clause === 'string' && clause.trim() !== '')
        || (clause && typeof clause === 'object' && !Array.isArray(clause) && Object.keys(clause).length > 0)
    ));
    return sanitiseSortDefinition(clauses);
};

const getDefaultSort = (storeSettings) => {
    if (storeSettings?.filters?.sort && storeSettings?.recommendedSort?.enabled) {
        return [...DEFAULT_RECOMMENDED_SORT];
    }

    return null;
};

const getSelectedSort = (activeFilters, storeSettings) => {
    const sortValue = normalizeProductListSort(activeFilters?.sort) || getDefaultSort(storeSettings);
    if (!sortValue) return [];

    const [field, order] = sortValue;

    if (field === 'recommended') {
        return getDefinitionSort(storeSettings?.recommendedSort?.query);
    }

    return [{[field]: order}];
};

const getGlobalRecommendedSort = (storeSettings) => {
    return getDefinitionSort(storeSettings?.globalRecommendedSort?.query);
};

const buildESQueryBody = (activeFilters, size = 24, storeSettings) => {
    let criteria = {query: {bool: {must: []}}, size: size};
    let aggsFilters = {};
    const resolvedFilters = resolveGroupFilters(activeFilters, storeSettings);
    const groupedFieldIds = new Set(getFilterGroups(storeSettings).map((group) => group.fieldId));

    for (const filter in resolvedFilters) {
        let bool;
        if (filter === 'sort') continue;
        else if (filter === 'page') criteria.from = (resolvedFilters[filter] - 1) * size;
        else if (filter.includes('-min') || filter.includes('-max')) {
            let id = filter.split('-');
            const minMax = id.pop() === 'min' ? 'gte' : 'lte';
            bool = {must: {range: {[id.join('-')]: {[minMax]: resolvedFilters[filter]}}}};
        } else if (filter.includes('keyword-search')) bool = {
            should: {
                multi_match: {
                    query: resolvedFilters[filter],
                    type: 'cross_fields',
                    operator: "and",
                    fields: ["title", ...(storeSettings?.fields?.search || [])]
                }
            }
        };
        else if (filter.includes('discount')) bool = {must: {range: {discountedSellingPrice: {gte: 0}}}}
        else {
            if (Array.isArray(resolvedFilters[filter]) && resolvedFilters[filter].length === 0) {
                bool = {must: {match_none: {}}};
            } else {
                bool = {should: [], minimum_should_match: 1}
            }
            const name = (['make', 'family', 'variant'].includes(filter)) ? `${filter}.keyword` : filter;
            const exactField = groupedFieldIds.has(filter)
                ? (storeSettings?.filterGroupQueryFields?.[filter] || (filter.endsWith('.keyword') ? filter : `${filter}.keyword`))
                : name;
            const buildFilterQuery = (value) => groupedFieldIds.has(filter)
                ? {term: {[exactField]: value}}
                : filter === 'locationCode.keyword'
                    ? {term: {[name]: {value, case_insensitive: true}}}
                    : {match_phrase: {[name]: value}};
            const values = Array.isArray(resolvedFilters[filter])
                ? resolvedFilters[filter]
                : [resolvedFilters[filter]];
            if (!(Array.isArray(resolvedFilters[filter]) && resolvedFilters[filter].length === 0)) {
                values.forEach((value) => {
                    bool.should.push(buildFilterQuery(value))
                });
            }
        }

        if (bool) {
            criteria.query.bool.must.push({bool})
            aggsFilters[filter] = {bool};
        }
    }

    const sort = [
        ...getGlobalRecommendedSort(storeSettings),
        ...getSelectedSort(resolvedFilters, storeSettings)
    ];
    if (sort.length) {
        criteria.sort = sort;
    }

    return {criteria, aggsFilters}
}

module.exports ={
    buildFiltersFromParam,
    buildESQueryBody,
    getDefaultSort,
    normalizeProductListSort,
    sanitiseSortDefinition
}
